import { BRAND_HOME_ARIA_LABEL, BRAND_LOGO_TITLE } from "@/lib/marketing-seo";
import { useTranslations } from "next-intl";
import Link from "next/link";
import type { ReactNode } from "react";
import { DotGridBackdrop } from "../../_components/marketing/dot-grid-backdrop";
import {
  BrandLogo,
  DEMO_HREF,
  HERO_PILL_ROW_STYLE,
  PricingHeroPill,
  T,
  heroPills,
  heroTagline,
} from "./pricing-shared";

/** Shared mobile section heading (one emphasised word + lead paragraph). */
export function MobileHeading({
  title,
  lead,
}: {
  readonly title: ReactNode;
  readonly lead: ReactNode;
}): ReactNode {
  return (
    <>
      <h2
        style={{
          fontFamily: T.display,
          fontWeight: 600,
          fontSize: 31,
          lineHeight: 1.04,
          letterSpacing: "-0.02em",
          color: T.ink,
          margin: 0,
        }}
      >
        {title}
      </h2>
      <p
        style={{
          fontFamily: T.sans,
          fontSize: 15,
          lineHeight: 1.55,
          color: "#575B63",
          margin: "12px 0 26px",
        }}
      >
        {lead}
      </p>
    </>
  );
}

/** Mobile hero: minimal nav, headline, big price, pills, stacked CTAs. */
export function MobileHero(): ReactNode {
  const tPricingShared = useTranslations("public.pricingShared");
  const t = useTranslations("public.pricingMobileHero");
  return (
    <div
      // i18n-exempt: data attribute, never rendered — section marker for the screenshot tooling
      data-screen-label="Hero"
      style={{
        position: "relative",
        overflow: "hidden",
        background: "#ffffff",
      }}
    >
      <DotGridBackdrop tone="light" focus="top" style={{ zIndex: 1 }} />
      <div
        style={{
          position: "relative",
          zIndex: 3,
          height: 58,
          display: "flex",
          alignItems: "center",
          padding: "0 18px",
        }}
      >
        <Link
          href="/"
          style={{ textDecoration: "none" }}
          aria-label={BRAND_HOME_ARIA_LABEL}
          title={BRAND_LOGO_TITLE}
        >
          <BrandLogo size={28} />
        </Link>
        <Link
          href="/login"
          className="mkpreise-btn-dark"
          style={{
            marginLeft: "auto",
            background: T.ink,
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            padding: "9px 15px",
            borderRadius: 8,
            textDecoration: "none",
            whiteSpace: "nowrap",
          }}
        >
          {t("anmelden")}
        </Link>
      </div>
      <div
        style={{
          position: "relative",
          zIndex: 2,
          textAlign: "center",
          padding: "26px 20px 120px",
        }}
      >
        <h1
          style={{
            fontFamily: T.display,
            fontWeight: 600,
            fontSize: 42,
            lineHeight: 1.0,
            letterSpacing: "-0.02em",
            margin: 0,
          }}
        >
          {t.rich("zahlAllesEnthalten", {
            br: () => <br />,
            em: (chunks) => <em style={{ fontStyle: "normal" }}>{chunks}</em>,
          })}
        </h1>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 14,
            margin: "26px 0 12px",
          }}
        >
          <span
            style={{
              fontFamily: T.display,
              fontWeight: 600,
              fontSize: 128,
              lineHeight: 0.8,
              letterSpacing: "-0.035em",
              color: T.ink,
            }}
          >
            49
          </span>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 8,
            }}
          >
            <span
              style={{
                fontFamily: T.display,
                fontStyle: "normal",
                fontWeight: 600,
                fontSize: 44,
                lineHeight: 0.82,
                color: T.ink,
              }}
            >
              {t("cent")}
            </span>
            <span
              style={{
                fontFamily: T.label,
                fontSize: 10.5,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "rgba(11,15,25,0.55)",
                whiteSpace: "nowrap",
              }}
            >
              {t("proEinheitMonat")}
            </span>
          </div>
        </div>
        <div
          style={{
            fontFamily: T.sans,
            fontSize: 14,
            color: "rgba(11,15,25,0.6)",
            margin: "0 auto 18px",
          }}
        >
          {t.rich("centFuerStellplaetze", {
            em: (chunks) => (
              <span
                style={{
                  fontFamily: T.display,
                  fontStyle: "normal",
                  fontWeight: 600,
                  fontSize: 17,
                  color: T.ink,
                }}
              >
                {chunks}
              </span>
            ),
          })}
        </div>
        <p
          style={{
            fontSize: 16,
            lineHeight: 1.5,
            color: "rgba(11,15,25,0.62)",
            margin: "0 auto 22px",
          }}
        >
          {heroTagline(tPricingShared)}
        </p>
        <div style={{ ...HERO_PILL_ROW_STYLE, margin: "0 auto 26px" }}>
          {heroPills(tPricingShared).map((pill) => (
            <PricingHeroPill key={pill} label={pill} />
          ))}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 14,
          }}
        >
          <Link
            href={DEMO_HREF}
            className="mkpreise-btn-dark"
            style={{
              background: T.ink,
              color: "#fff",
              padding: "14px 26px",
              borderRadius: 9,
              fontSize: 15,
              fontWeight: 600,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {t("demoBuchen")} <span style={{ fontSize: 12 }}>↗</span>
          </Link>
          <a
            href={t("enthalten")}
            style={{
              color: T.ink,
              fontSize: 14.5,
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            {t("allesDabei")}
          </a>
        </div>
      </div>
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 120,
          zIndex: 4,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0) 0%, #FFFFFF 92%)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}
